import { useEffect, useState } from 'react';
import { cancelIdleScheduleHandle, scheduleIdleCallbackWithFallback } from '../../utils/browser/scheduleIdleCallbackWithFallback';

/**
 * SearchPanel, loaded on demand. Every page's navbar and mobile bottom nav host the panel, but it
 * only renders once opened (and is never server-rendered), so its ~55 KB of code doesn't need to
 * be in the bundle a page starts with. Hosts call useSearchPanelComponent(), which fetches it when
 * the browser goes idle after the page loads (so it's normally there long before anyone opens the
 * panel) or straight away once the panel is wanted. Until it arrives, hosts keep their closed bar
 * showing; the mobile takeover shows a plain input instead so the tap still raises the keyboard.
 */
type SearchPanelModule = typeof import('./SearchPanel');
export type SearchPanelComponent = SearchPanelModule['SearchPanel'];

let loaded: SearchPanelModule | null = null;
let pending: Promise<SearchPanelModule> | null = null;

export function preloadSearchPanel(): Promise<SearchPanelModule> {
  if (loaded) return Promise.resolve(loaded);
  pending ??= import('./SearchPanel').then(
    (mod) => (loaded = mod),
    (err) => {
      pending = null;
      throw err;
    },
  );
  return pending;
}

/** Idle-time cap before the background fetch starts anyway. */
const IDLE_PRELOAD_TIMEOUT_MS = 4000;

/**
 * The SearchPanel component once its code has loaded, else null. Starts loading at idle after
 * mount, or immediately while `wanted` is true.
 */
export function useSearchPanelComponent(wanted: boolean): SearchPanelComponent | null {
  const [component, setComponent] = useState<SearchPanelComponent | null>(() => loaded?.SearchPanel ?? null);

  useEffect(() => {
    if (component) return;
    let live = true;
    const load = () => {
      preloadSearchPanel().then(
        (mod) => {
          if (live) setComponent(() => mod.SearchPanel);
        },
        () => {},
      );
    };
    if (wanted) {
      load();
      return () => {
        live = false;
      };
    }
    const handle = scheduleIdleCallbackWithFallback(load, { timeout: IDLE_PRELOAD_TIMEOUT_MS });
    return () => {
      live = false;
      cancelIdleScheduleHandle(handle);
    };
  }, [component, wanted]);

  return component;
}
